/* Verified product data is read in the current storefront context. No cart writes,
   inferred dimensions, inferred energy usage, persistent catalog cache or private data. */
(function (root, factory) {
  'use strict';
  const core = factory();
  if (typeof module === 'object' && module.exports) module.exports = core;
  if (!root?.document || root.SaberApplianceTools) return;
  root.SaberApplianceTools = core;
  const document = root.document;
  const preferences = { rates: Object.create(null), unit: 'in' };
  let unique = 0;
  const el = (tag, text, attrs = {}) => {
    const node = document.createElement(tag);
    if (text !== undefined) node.textContent = String(text);
    for (const [key, value] of Object.entries(attrs)) node.setAttribute(key, String(value));
    return node;
  };
  const dictionary = node => {
    let entries;
    try { entries = JSON.parse(node.dataset.i18n || '{}'); } catch (_) { entries = {}; }
    return (key, values = {}) => String(entries[key] || key).replace(/\[\[(\w+)\]\]/g, (token, name) => Object.hasOwn(values, name) ? String(values[name]) : token);
  };
  const field = (label, input) => {
    const wrapper = el('label', undefined, { class: 'ss-appliance-field' });
    wrapper.append(el('span', label), input);
    return wrapper;
  };
  const button = (label, secondary = false) => el('button', label, { type: 'button', class: 'ss-appliance-button' + (secondary ? ' ss-appliance-button--secondary' : '') });
  const message = (node, text, error = false) => {
    node.textContent = text;
    node.hidden = !text;
    node.setAttribute('role', error ? 'alert' : 'status');
    node.classList.toggle('is-error', error);
  };
  const numberText = (value, locale, digits = 6) => new Intl.NumberFormat(locale || 'en', { maximumFractionDigits: digits }).format(value);
  const currencyText = (value, locale, currency) => {
    try { return new Intl.NumberFormat(locale || 'en', { style: 'currency', currency }).format(value); }
    catch (_) { return Number(value).toFixed(2) + ' ' + String(currency); }
  };
  const rateText = (value, locale, currency) => {
    try { return new Intl.NumberFormat(locale || 'en', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 12 }).format(value); }
    catch (_) { return String(value) + ' ' + String(currency); }
  };
  const source = (record, translate) => {
    const wrapper = el('div', undefined, { class: 'ss-appliance-source' });
    const link = el('a', record.source.title, { href: record.source.url, target: '_blank', rel: 'noopener noreferrer' });
    wrapper.append(link, el('span', translate('source_reviewed', { date: record.source.reviewedOn })), el('span', translate('source_model', { model: record.model })));
    return wrapper;
  };
  function nativeSelectionValid(widget) {
    const product = widget.closest('[id^="ProductPage-"]');
    if (!product) return true;
    if (product.dataset.selectionUnavailable === 'true' || product.dataset.variantLoading === 'true') return false;
    const active = product.querySelector('product-form form [name="id"]');
    return !active || String(active.value) === String(widget.snapshot.variant.id);
  }
  class ApplianceTools extends root.HTMLElement {
    connectedCallback() {
      if (this.active) return;
      this.active = true;
      this.events = new AbortController();
      this.t = dictionary(this);
      this.currency = /^[A-Z]{3}$/.test(this.dataset.currency || '') ? this.dataset.currency : 'USD';
      this.locale = this.dataset.locale || 'en';
      try { this.snapshot = core.snapshot(JSON.parse(this.dataset.snapshot || '{}'), location.origin); }
      catch (_) { this.replaceChildren(el('p', this.t('load_error'), { role: 'alert' })); return; }
      this.render();
    }
    disconnectedCallback() { this.events?.abort(); this.active = false; }
    listen(node, event, fn) { node.addEventListener(event, fn, { signal: this.events.signal }); }
    checkSelection(status) {
      if (nativeSelectionValid(this)) return true;
      message(status, this.t('selection_unavailable'), true);
      return false;
    }
    render() {
      const grid = el('div', undefined, { class: 'ss-appliance-grid' });
      grid.append(this.energyCard(), this.fitCard());
      this.replaceChildren(grid);
    }
    energyCard() {
      const card = el('article', undefined, { class: 'ss-appliance-card' });
      card.append(el('h3', this.t('annual_title')));
      let energy;
      try { energy = core.energy(this.snapshot); }
      catch (error) {
        card.append(el('p', this.t(error.code || 'invalid_energy'), { class: 'ss-appliance-muted' }));
        this.resources(card, false); return card;
      }
      card.append(el('p', this.t('annual_usage') + ': ' + numberText(energy.annualKwh, this.locale) + ' ' + this.t('annual_kwh_unit'), { class: 'ss-appliance-published' }), source(energy, this.t));
      const form = el('form', undefined, { class: 'ss-appliance-form' });
      const rate = el('input', undefined, { type: 'number', min: '0', max: '1000', step: 'any', inputmode: 'decimal', required: '', 'aria-label': this.t('rate_label') });
      rate.value = preferences.rates[this.currency] ?? '';
      const rateField = field(this.t('rate_label') + ' (' + this.currency + ')', rate);
      const hint = el('p', this.t('rate_hint', { currency: this.currency }), { class: 'ss-appliance-hint' });
      const submit = button(this.t('estimate_button')); submit.type = 'submit';
      const result = el('div', undefined, { class: 'ss-appliance-result', role: 'status', 'aria-live': 'polite', hidden: '' });
      const status = el('p', undefined, { role: 'alert', hidden: '' });
      form.append(rateField, hint, submit, status, result);
      this.listen(form, 'submit', event => {
        event.preventDefault();
        if (!this.checkSelection(status)) return;
        try {
          energy = core.energy(this.snapshot);
          const entered = core.input(rate.value, true, 'rate_required');
          if (entered > 1000) throw core.fail('rate_required');
          const annual = core.annualCost(energy.annualKwh, entered);
          preferences.rates[this.currency] = rate.value;
          result.replaceChildren(el('strong', this.t('annual_result', { amount: currencyText(annual, this.locale, this.currency) })), el('span', this.t('monthly_result', { amount: currencyText(annual / 12, this.locale, this.currency) })), el('span', this.t('cost_basis', { usage: numberText(energy.annualKwh, this.locale), rate: rateText(entered, this.locale, this.currency) })));
          result.hidden = false; message(status, '');
        } catch (error) { result.hidden = true; message(status, this.t(error.code || 'rate_required'), true); }
      });
      card.append(form, el('p', this.t('cost_limit'), { class: 'ss-appliance-hint' }));
      return card;
    }
    fitCard() {
      const card = el('article', undefined, { class: 'ss-appliance-card' });
      card.append(el('h3', this.t('fit_title')), el('p', this.t('fit_intro'), { class: 'ss-appliance-muted' }));
      let record;
      try { record = core.fitData(this.snapshot); }
      catch (error) { card.append(el('p', this.t(error.code || 'fit_invalid'), { class: 'ss-appliance-muted' })); this.resources(card, true); return card; }
      card.append(source(record, this.t));
      const dimensions = el('p', this.t('product_dimensions') + ': ' + ['width', 'height', 'depth'].map(axis => this.t(axis) + ' ' + numberText(record.dimensions[axis], this.locale) + ' ' + record.unit).join(' · '), { class: 'ss-appliance-published' });
      card.append(dimensions);
      const form = el('form', undefined, { class: 'ss-appliance-form' });
      const unit = el('select', undefined, { 'aria-label': this.t('measurement_unit') });
      unit.append(el('option', this.t('inches'), { value: 'in' }), el('option', this.t('millimeters'), { value: 'mm' }));
      unit.value = preferences.unit;
      const fields = el('div', undefined, { class: 'ss-appliance-fields' });
      const inputs = {};
      for (const [axis, label] of [['width', 'opening_width'], ['height', 'opening_height'], ['depth', 'opening_depth'], ['front', 'front_space']]) {
        const input = el('input', undefined, { type: 'number', min: axis === 'front' ? '0' : '0.001', max: '1000000', step: 'any', inputmode: 'decimal', required: '', 'aria-label': this.t(label), 'data-appliance-opening': axis });
        if (axis === 'front') input.value = '0';
        fields.append(field(this.t(label), input)); inputs[axis] = input;
      }
      const submit = button(this.t('fit_button')); submit.type = 'submit';
      const result = el('div', undefined, { class: 'ss-appliance-result', role: 'status', 'aria-live': 'polite', hidden: '' });
      const status = el('p', undefined, { role: 'alert', hidden: '' });
      form.append(field(this.t('measurement_unit'), unit), fields, submit, status, result);
      this.listen(form, 'submit', event => {
        event.preventDefault();
        if (!this.checkSelection(status)) return;
        try {
          record = core.fitData(this.snapshot);
          const opening = Object.fromEntries(Object.entries(inputs).map(([key, input]) => [key, core.input(input.value, key === 'front')]));
          const answer = core.checkFit(record, opening, unit.value);
          preferences.unit = unit.value;
          result.replaceChildren(el('strong', this.t(answer.meets ? 'fit_meets' : 'fit_fails')));
          result.classList.toggle('is-error', !answer.meets);
          const requirements = el('ul');
          for (const axis of ['width', 'height', 'depth', 'front']) {
            requirements.append(el('li', this.t('fit_required', { axis: this.t(axis), value: numberText(answer.required[axis], this.locale), unit: unit.value })));
            if (answer.shortages[axis] > 0) requirements.append(el('li', this.t('fit_short', { axis: this.t(axis), value: numberText(answer.shortages[axis], this.locale), unit: unit.value })));
          }
          result.append(requirements); result.hidden = false; message(status, '');
        } catch (error) { result.hidden = true; message(status, this.t(error.code || 'fit_inputs'), true); }
      });
      card.append(form, el('p', this.t('clearance_note'), { class: 'ss-appliance-hint' }), el('p', this.t('fit_limit'), { class: 'ss-appliance-hint' }));
      this.resources(card, true); return card;
    }
    resources(card, measuring) {
      const links = el('div', undefined, { class: 'ss-appliance-resource-links' });
      if (measuring && this.dataset.measureUrl) links.append(el('a', this.t('measure_link'), { href: this.dataset.measureUrl }));
      if (this.dataset.documentsUrl) links.append(el('a', this.t('documents_link'), { href: this.dataset.documentsUrl }));
      if (links.children.length) card.append(links);
    }
  }
  async function readProduct(value, signal) {
    const url = core.productUrl(value, location.origin);
    const requested = url.searchParams.get('variant');
    url.searchParams.set('view', 'appliance-tools');
    const response = await fetch(url.href, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' }, signal });
    if (!response.ok) throw core.fail('load_error');
    let json;
    try { json = JSON.parse(await response.text()); } catch (_) { throw core.fail('load_error'); }
    const result = core.snapshot(json, location.origin);
    if (requested && result.variant.id !== requested) throw core.fail('bad_variant_url');
    if (core.productUrl(result.product.url, location.origin).pathname !== url.pathname) throw core.fail('bad_product_url');
    return result;
  }
  class ApplianceWorkspace extends root.HTMLElement {
    connectedCallback() {
      if (this.active) return;
      this.active = true; this.events = new AbortController(); this.rows = [];
      this.t = dictionary(this); this.locale = this.dataset.locale || 'en'; this.currency = /^[A-Z]{3}$/.test(this.dataset.currency || '') ? this.dataset.currency : 'USD';
      this.render();
      const requested = new URL(location.href).searchParams.get('product');
      if (requested) {
        this.rows[0].url.value = requested;
        this.loadRow(this.rows[0]).then(snapshot => { if (snapshot) this.renderFit(snapshot); });
      }
    }
    disconnectedCallback() { this.active = false; this.events?.abort(); this.fitController?.abort(); this.rows?.forEach(row => row.controller?.abort()); }
    listen(node, event, fn) { node.addEventListener(event, fn, { signal: this.events.signal }); }
    render() {
      const section = el('section', undefined, { class: 'ss-appliance-comparison' });
      section.append(el('h2', this.t('compare_title')), el('p', this.t('compare_intro')));
      this.rate = el('input', undefined, { type: 'number', min: '0', max: '1000', step: 'any', inputmode: 'decimal', 'aria-label': this.t('rate_label') });
      this.rate.value = preferences.rates[this.currency] ?? '';
      section.append(field(this.t('rate_label') + ' (' + this.currency + ')', this.rate), el('p', this.t('rate_hint', { currency: this.currency }), { class: 'ss-appliance-hint' }));
      this.rowsNode = el('div', undefined, { class: 'ss-appliance-comparison-rows' });
      const actions = el('div', undefined, { class: 'ss-appliance-actions' });
      this.addButton = button(this.t('add_appliance'), true); this.comparedButton = button(this.t('use_compared'), true); this.calculateButton = button(this.t('compare_button'));
      actions.append(this.addButton, this.comparedButton, this.calculateButton);
      this.compareStatus = el('p', undefined, { role: 'status', 'aria-live': 'polite', hidden: '' });
      this.results = el('div', undefined, { class: 'ss-appliance-comparison-result', hidden: '' });
      section.append(this.rowsNode, actions, this.compareStatus, this.results, el('p', this.t('cost_limit'), { class: 'ss-appliance-hint' }));
      const fit = el('section', undefined, { class: 'ss-appliance-fit-workspace' });
      fit.append(el('h2', this.t('fit_workspace_title')), el('p', this.t('fit_workspace_intro')));
      this.fitInput = el('input', undefined, { type: 'url', 'aria-label': this.t('product_link'), placeholder: location.origin + '/products/…' });
      const fitLoad = button(this.t('fit_load'));
      this.fitStatus = el('p', undefined, { role: 'status', 'aria-live': 'polite', hidden: '' }); this.fitResult = el('div');
      this.fitResult.append(el('p', this.t('empty_fit'), { class: 'ss-appliance-muted' }));
      fit.append(field(this.t('product_link'), this.fitInput), fitLoad, this.fitStatus, this.fitResult);
      this.replaceChildren(section, fit);
      this.addRow(); this.addRow();
      this.listen(this.addButton, 'click', () => { if (this.rows.length < 4) this.addRow(); });
      this.listen(this.comparedButton, 'click', () => this.useCompared());
      this.listen(this.calculateButton, 'click', () => this.calculate());
      this.listen(this.rate, 'input', () => { this.results.hidden = true; this.rateVersion = (this.rateVersion || 0) + 1; message(this.compareStatus, ''); });
      this.listen(fitLoad, 'click', async () => {
        this.fitController?.abort(); const controller = this.fitController = new AbortController(); const value = this.fitInput.value;
        fitLoad.disabled = true; message(this.fitStatus, this.t('loading'));
        const timeout = setTimeout(() => controller.abort(), 15000);
        try { const snapshot = await readProduct(value, controller.signal); if (this.active && this.fitInput.value === value) { this.renderFit(snapshot); message(this.fitStatus, this.t('load_success')); } }
        catch (error) { if (this.active && this.fitInput.value === value) { this.fitResult.replaceChildren(); message(this.fitStatus, this.t(error.code || 'load_error'), true); } }
        finally { clearTimeout(timeout); if (this.active) fitLoad.disabled = false; }
      });
      this.listen(this.fitInput, 'input', () => { this.fitController?.abort(); this.fitResult.replaceChildren(); message(this.fitStatus, ''); });
    }
    addRow() {
      if (this.rows.length >= 4) return;
      const row = { id: ++unique, generation: 0, snapshot: null };
      row.element = el('article', undefined, { class: 'ss-appliance-entry' });
      const heading = el('div', undefined, { class: 'ss-appliance-entry-heading' });
      row.heading = el('h3'); row.remove = button(this.t('remove_appliance'), true); heading.append(row.heading, row.remove);
      row.mode = el('select', undefined, { 'aria-label': this.t('entry_mode') }); row.mode.append(el('option', this.t('product_mode'), { value: 'product' }), el('option', this.t('manual_mode'), { value: 'manual' }));
      row.productFields = el('div', undefined, { class: 'ss-appliance-entry-fields' });
      row.url = el('input', undefined, { type: 'url', 'aria-label': this.t('product_link'), placeholder: location.origin + '/products/…' });
      row.load = button(this.t('load_product'), true); row.productInfo = el('div'); row.status = el('p', undefined, { role: 'status', 'aria-live': 'polite', hidden: '' });
      row.productFields.append(field(this.t('product_link'), row.url), row.load, row.status, row.productInfo);
      row.manualFields = el('div', undefined, { class: 'ss-appliance-entry-fields', hidden: '' });
      row.name = el('input', undefined, { type: 'text', maxlength: '160', 'aria-label': this.t('appliance_name') });
      row.kwh = el('input', undefined, { type: 'number', min: '0.001', step: 'any', inputmode: 'decimal', 'aria-label': this.t('manual_usage') });
      row.manualUrl = el('input', undefined, { type: 'url', 'aria-label': this.t('manual_source') });
      row.manualFields.append(field(this.t('appliance_name'), row.name), field(this.t('manual_usage'), row.kwh), field(this.t('manual_source'), row.manualUrl), el('p', this.t('manual_note'), { class: 'ss-appliance-hint' }));
      row.element.append(heading, field(this.t('entry_mode'), row.mode), row.productFields, row.manualFields);
      this.rows.push(row); this.rowsNode.append(row.element);
      if (this.compareStatus) message(this.compareStatus, '');
      this.listen(row.remove, 'click', () => { if (this.rows.length <= 2) return; row.controller?.abort(); row.generation++; row.element.remove(); this.rows = this.rows.filter(other => other !== row); this.updateRows(); this.results.hidden = true; message(this.compareStatus, ''); });
      this.listen(row.mode, 'change', () => {
        row.controller?.abort(); row.generation++; row.load.disabled = false; row.snapshot = null; row.productInfo.replaceChildren(); message(row.status, '');
        row.manualFields.hidden = row.mode.value !== 'manual'; row.productFields.hidden = row.mode.value !== 'product'; this.results.hidden = true; message(this.compareStatus, '');
      });
      this.listen(row.url, 'input', () => { row.controller?.abort(); row.generation++; row.load.disabled = false; row.snapshot = null; row.productInfo.replaceChildren(); message(row.status, ''); this.results.hidden = true; message(this.compareStatus, ''); });
      this.listen(row.load, 'click', () => this.loadRow(row));
      for (const input of [row.name, row.kwh, row.manualUrl]) this.listen(input, 'input', () => { this.results.hidden = true; message(this.compareStatus, ''); });
      this.updateRows(); return row;
    }
    updateRows() {
      this.addButton.disabled = this.rows.length >= 4;
      this.rows.forEach((row, index) => { row.heading.textContent = this.t('appliance_number', { number: index + 1 }); row.remove.disabled = this.rows.length <= 2; });
    }
    async loadRow(row) {
      if (!this.active || row.mode.value !== 'product') return null;
      row.controller?.abort(); const controller = row.controller = new AbortController(); const generation = ++row.generation;
      row.load.disabled = true; row.snapshot = null; row.productInfo.replaceChildren(); message(row.status, this.t('loading'));
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const snapshot = await readProduct(row.url.value, controller.signal);
        if (!this.active || generation !== row.generation || !row.element.isConnected) return null;
        row.snapshot = snapshot;
        const link = el('a', snapshot.product.title + (snapshot.variant.title && snapshot.variant.title !== 'Default Title' ? ' — ' + snapshot.variant.title : ''), { href: core.variantUrl(snapshot, location.origin), class: 'ss-appliance-product-link' });
        row.productInfo.append(link);
        if (snapshot.variant.sku) row.productInfo.append(el('p', this.t('sku') + ': ' + snapshot.variant.sku, { class: 'ss-appliance-hint' }));
        try { const energy = core.energy(snapshot); row.productInfo.append(el('p', numberText(energy.annualKwh, this.locale) + ' ' + this.t('annual_kwh_unit')), source(energy, this.t)); message(row.status, this.t('load_success')); }
        catch (error) { message(row.status, this.t(error.code || 'invalid_energy'), true); }
        const useFit = button(this.t('fit_use'), true); this.listen(useFit, 'click', async () => { const fresh = await this.loadRow(row); if (fresh) this.renderFit(fresh); }); row.productInfo.append(useFit);
        return snapshot;
      } catch (error) { if (this.active && generation === row.generation) message(row.status, this.t(error.code || 'load_error'), true); return null; }
      finally { clearTimeout(timeout); if (this.active && generation === row.generation) row.load.disabled = false; }
    }
    renderFit(snapshot) {
      const heading = el('div', undefined, { class: 'ss-appliance-fit-product-heading' });
      heading.append(el('h3', snapshot.product.title), el('a', this.t('back_product'), { href: core.variantUrl(snapshot, location.origin) }));
      const widget = el('saber-appliance-tools', undefined, { 'data-snapshot': JSON.stringify(snapshot), 'data-i18n': this.dataset.i18n, 'data-currency': this.currency, 'data-locale': this.locale, 'data-measure-url': this.dataset.measureUrl || '', 'data-documents-url': core.variantUrl(snapshot, location.origin) + '#product-documents-' + snapshot.product.id });
      this.fitResult.replaceChildren(heading, widget); this.fitInput.value = core.variantUrl(snapshot, location.origin);
    }
    async useCompared() {
      const selection = Object.values(root.SaberCompare?.read?.() || {}).slice(0, 4);
      if (!selection.length) { message(this.compareStatus, this.t('compared_empty'), true); return; }
      this.rows.forEach(row => row.controller?.abort()); this.rows = []; this.rowsNode.replaceChildren(); this.results.hidden = true;
      for (const selected of selection) { const row = this.addRow(); row.url.value = selected.url; }
      while (this.rows.length < 2) this.addRow();
      await Promise.all(this.rows.filter(row => row.url.value).map(row => this.loadRow(row)));
      if (this.active) message(this.compareStatus, this.t('compared_added'));
    }
    async calculate() {
      if (this.busy) return;
      this.results.hidden = true; this.busy = true; this.calculateButton.disabled = true;
      try {
        const rateVersion = this.rateVersion || 0;
        const rate = core.input(this.rate.value, true, 'rate_required'); if (rate > 1000) throw core.fail('rate_required');
        await Promise.all(this.rows.filter(row => row.mode.value === 'product').map(row => this.loadRow(row)));
        if (!this.active || rateVersion !== (this.rateVersion || 0)) return;
        const rows = this.rows.map(row => {
          if (row.mode.value === 'manual') {
            const name = row.name.value.trim(); const usage = core.input(row.kwh.value, false, 'invalid_manual');
            if (!name || name.length > 160 || (row.manualUrl.value && !core.sourceUrl(row.manualUrl.value))) throw core.fail('invalid_manual');
            return { name, usage, origin: this.t('manual_data'), url: row.manualUrl.value || '', annual: core.annualCost(usage, rate) };
          }
          if (!row.snapshot) throw core.fail('compare_need_two');
          const energy = core.energy(row.snapshot);
          return { name: row.snapshot.product.title + (row.snapshot.variant.title && row.snapshot.variant.title !== 'Default Title' ? ' — ' + row.snapshot.variant.title : ''), usage: energy.annualKwh, origin: this.t('verified_data'), url: energy.source.url, annual: core.annualCost(energy.annualKwh, rate) };
        });
        if (rows.length < 2 || rows.length > 4) throw core.fail('compare_need_two');
        const table = el('table', undefined, { class: 'ss-appliance-table' });
        const head = el('thead'); const header = el('tr');
        for (const key of ['appliance', 'annual_usage', 'annual_cost', 'monthly_cost', 'difference', 'data_origin']) header.append(el('th', this.t(key), { scope: 'col' }));
        head.append(header); table.append(head);
        const body = el('tbody');
        rows.forEach(row => {
          const tr = el('tr'); tr.append(el('th', row.name, { scope: 'row' }), el('td', numberText(row.usage, this.locale) + ' ' + this.t('annual_kwh_unit')), el('td', currencyText(row.annual, this.locale, this.currency)), el('td', currencyText(row.annual / 12, this.locale, this.currency)), el('td', currencyText(row.annual - rows[0].annual, this.locale, this.currency)));
          const provenance = el('td', row.origin); if (row.url) provenance.append(el('a', this.t('source'), { href: row.url, target: '_blank', rel: 'noopener noreferrer' })); tr.append(provenance); body.append(tr);
        });
        table.append(body); this.results.replaceChildren(table); this.results.hidden = false;
        preferences.rates[this.currency] = this.rate.value; message(this.compareStatus, this.t('compare_updated'));
      } catch (error) { message(this.compareStatus, this.t(error.code || 'compare_need_two'), true); }
      finally { this.busy = false; if (this.active) this.calculateButton.disabled = false; }
    }
  }
  if (!root.customElements.get('saber-appliance-tools')) root.customElements.define('saber-appliance-tools', ApplianceTools);
  if (!root.customElements.get('saber-appliance-workspace')) root.customElements.define('saber-appliance-workspace', ApplianceWorkspace);
})(typeof window === 'undefined' ? null : window, function () {
  'use strict';
  const numeric = (value, zero = false) => typeof value === 'number' && Number.isFinite(value) && (zero ? value >= 0 : value > 0) && value <= 1000000000;
  const fail = code => Object.assign(new Error(code), { code });
  const id = value => typeof value === 'string' && /^[1-9]\d{0,19}$/.test(value);
  function date(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const parsed = new Date(value + 'T00:00:00Z');
    return Number.isFinite(parsed.valueOf()) && parsed.toISOString().slice(0, 10) === value ? value : null;
  }
  function today(now = new Date()) {
    const parts = new Intl.DateTimeFormat('en', { timeZone: 'America/Denver', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
    const get = type => parts.find(part => part.type === type).value;
    return get('year') + '-' + get('month') + '-' + get('day');
  }
  function sourceUrl(value) {
    if (typeof value !== 'string' || value.length > 2000 || !value.trim()) return null;
    try { const url = new URL(value); return url.protocol === 'https:' && !url.username && !url.password ? url.href : null; } catch (_) { return null; }
  }
  function productUrl(value, origin) {
    if (typeof value !== 'string' || value.length > 2500 || !value.trim()) throw fail('bad_product_url');
    let url; try { url = new URL(value, origin); } catch (_) { throw fail('bad_product_url'); }
    if (url.origin !== origin || url.username || url.password || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9][a-z0-9_-]{0,199}\/?$/i.test(url.pathname)) throw fail('bad_product_url');
    const variant = url.searchParams.get('variant'); if (variant !== null && !id(variant)) throw fail('bad_variant_url');
    url.search = ''; url.hash = ''; if (variant) url.searchParams.set('variant', variant);
    return url;
  }
  function snapshot(data, origin) {
    if (!data || data.schema !== 'saber-appliance-tools-v1' || !data.product || !data.variant || !id(data.product.id) || !id(data.variant.id) || typeof data.product.title !== 'string' || !data.product.title || data.product.title.length > 500 || !Number.isSafeInteger(data.product.variantCount) || data.product.variantCount < 1 || typeof data.variant.sku !== 'string' || typeof data.variant.model !== 'string' || typeof data.variant.title !== 'string') throw fail('load_error');
    productUrl(data.product.url, origin);
    for (const kind of ['energy', 'fit']) if (data[kind] !== null && (typeof data[kind] !== 'object' || Array.isArray(data[kind]))) throw fail('load_error');
    return data;
  }
  function record(data, kind, currentDay = today()) {
    const value = data[kind], missing = kind === 'energy' ? 'missing_energy' : 'fit_missing', invalid = kind === 'energy' ? 'invalid_energy' : 'fit_invalid';
    if (!value) throw fail(missing);
    if (value.version !== 1 || value.verified !== true || !['variant', 'product'].includes(value.origin) || typeof value.model !== 'string' || !value.model.trim() || value.model.length > 160) throw fail(invalid);
    if (value.origin === 'product' && data.product.variantCount > 1 && value.allVariantsVerified !== true && (!Array.isArray(value.variantIds) || !value.variantIds.some(candidate => candidate === data.variant.id))) throw fail('variant_mismatch');
    const model = text => text.trim().replace(/\s+/g, ' ').toUpperCase();
    if (data.variant.model && model(value.model) !== model(data.variant.model)) throw fail('model_mismatch');
    if (value.sku && value.sku !== data.variant.sku) throw fail('variant_mismatch');
    const source = value.source;
    if (!source || typeof source.title !== 'string' || !source.title.trim() || source.title.length > 200 || !sourceUrl(source.url) || !date(source.reviewedOn) || source.reviewedOn > currentDay) throw fail('source_unavailable');
    if (source.validUntil && (!date(source.validUntil) || source.validUntil < currentDay || source.validUntil < source.reviewedOn)) throw fail('expired_source');
    if (source.updatedAt) {
      const rawUpdate = String(source.updatedAt);
      const timestamp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(rawUpdate) ? new Date(rawUpdate) : null;
      const updated = date(rawUpdate) || (timestamp && Number.isFinite(timestamp.valueOf()) && date(rawUpdate.slice(0, 10)) ? today(timestamp) : null);
      if (!updated || updated > source.reviewedOn || updated > currentDay) throw fail('expired_source');
    }
    return value;
  }
  function energy(data, currentDay) {
    const value = record(data, 'energy', currentDay);
    if (value.basis !== 'kwh_per_year' || !numeric(value.annualKwh)) throw fail('invalid_energy');
    return value;
  }
  function fitData(data, currentDay) {
    const value = record(data, 'fit', currentDay);
    if (!['in', 'mm'].includes(value.unit) || value.connectionsIncluded !== true || value.doorClearanceIncluded !== true || !value.dimensions || !value.clearances || !['width', 'height', 'depth'].every(axis => numeric(value.dimensions[axis])) || !['left', 'right', 'top', 'bottom', 'rear', 'front'].every(side => numeric(value.clearances[side], true))) throw fail('fit_invalid');
    return value;
  }
  function input(value, zero = false, errorCode = 'fit_inputs') {
    if (typeof value !== 'number' && typeof value !== 'string') throw fail(errorCode);
    if (typeof value === 'string' && !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(value.trim())) throw fail(errorCode);
    const number = typeof value === 'number' ? value : Number(value);
    if (!numeric(number, zero)) throw fail(errorCode);
    return number;
  }
  function annualCost(usage, rate) {
    if (!numeric(usage) || !numeric(rate, true)) throw fail('rate_required');
    const result = usage * rate;
    if (!Number.isFinite(result)) throw fail('rate_required');
    return result;
  }
  function convert(value, from, to) {
    if (!numeric(value, true) || !['in', 'mm'].includes(from) || !['in', 'mm'].includes(to)) throw fail('fit_inputs');
    return from === to ? value : from === 'in' ? value * 25.4 : value / 25.4;
  }
  function checkFit(value, opening, unit) {
    if (!['in', 'mm'].includes(unit) || !['width', 'height', 'depth'].every(axis => numeric(opening[axis])) || !numeric(opening.front, true)) throw fail('fit_inputs');
    const raw = { width: value.dimensions.width + value.clearances.left + value.clearances.right, height: value.dimensions.height + value.clearances.top + value.clearances.bottom, depth: value.dimensions.depth + value.clearances.rear, front: value.clearances.front };
    const required = Object.fromEntries(Object.entries(raw).map(([axis, amount]) => [axis, convert(amount, value.unit, unit)]));
    const shortages = Object.fromEntries(Object.keys(required).map(axis => [axis, Math.max(0, required[axis] - opening[axis])]));
    const meets = Object.keys(required).every(axis => shortages[axis] <= 1e-7);
    return { required, shortages, meets, unit };
  }
  function variantUrl(data, origin) { const url = productUrl(data.product.url, origin); url.searchParams.set('variant', data.variant.id); return url.href; }
  return Object.freeze({ fail, date, today, sourceUrl, productUrl, snapshot, record, energy, fitData, input, annualCost, convert, checkFit, variantUrl });
});
